import { CustomTestEdit as CustomTestEditV1, CustomTestsSearch as CustomTestsSearchV1, CustomTests as CustomTestsV1 } from 'pages/custom_tests_v1';
import { BrowserRouter, Navigate, Route, useLocation } from 'react-router-dom';
import { GuardedRoutes, RouteGuard } from 'app/router/route_guard';
import { RequireAccess } from 'app/router/RequireAccess';
import { HelpSidebar } from 'components/help_sidebar';
import * as Pages from 'pages';
import { useApp } from 'app';

// Performs an app-level redirect, passing along any current querystring params
export const RedirectWithQuery = ({ to }: { to: string }) => {

    const { search } = useLocation();
    return <Navigate replace to={`${to}${search}`} />;

};

// Gates the redesigned Partners landing page behind the Admin.Partner.PartnerManagement feature flag.
// Users without the flag fall back to the legacy lookup → create/edit flow.
const PartnersRouteSwitch = () => {

    const { isFeatureEnabled } = useApp();
    const { search } = useLocation();

    if (isFeatureEnabled('Admin.Partner.PartnerManagement')) return <Pages.Partners />;

    // Forward to lookup, preserving PType and defaulting RefPage to the legacy create/edit page
    const params = new URLSearchParams(search);
    const pType = params.get('PType') ?? params.get('ptype') ?? '';
    if (!params.has('RefPage') && pType) {

        params.set('RefPage', `/partners/create?PageState=Edit&PType=${pType}`);

    }
    const qs = params.toString();
    return <Navigate replace to={`/partners/lookup${qs ? `?${qs}` : ''}`} />;

};

export const AppRouter = () => {

    const { userId } = useApp();

    const DashboardOrLogin = () => userId ? <Pages.Dashboard /> : <Pages.Login />;

    return (
        <BrowserRouter>
            <RouteGuard>
                {/* We want the sidebar nested here so that we can utilize in-app links within its content */}
                <HelpSidebar>
                    <GuardedRoutes>

                        <Route element={<RedirectWithQuery to={'/'}/>} path={'Default.aspx'} />
                        <Route index element={<DashboardOrLogin/>} />

                        <Route element={<Pages.MyAccount />} path={'/account'} />

                        <Route path={'/access-codes'}>
                            <Route
                                index
                                element={(
                                    <RequireAccess itemId={'lbCodesSearchEdit'}>
                                        <Pages.AccessCodes />
                                    </RequireAccess>
                                )}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbCodesSearchEdit'}>
                                        <Pages.AccessCodes />
                                    </RequireAccess>
                                )} path={':id'}
                            />
                        </Route>

                        <Route path={'/associations'}>

                            <Route
                                element={(
                                    <RequireAccess itemId={'lbCollectionEdit'}>
                                        <Pages.CollectionsWrapper />
                                    </RequireAccess>
                                )}
                            >
                                <Route index element={<Pages.Collections />} />
                                <Route element={<Pages.CollectionEdit />} path={'edit'} />
                            </Route>

                        </Route>

                        <Route
                            element={(
                                <RequireAccess itemId={'lbMenuResources'}>
                                    <Pages.Resources />
                                </RequireAccess>
                            )} path={'course-resources'}
                        />

                        {/* Classic Custom Tests (V1) — the side-nav destination. A banner on the
                            lookup page links to the rebuilt experience at /custom-tests-v2. */}
                        <Route
                            element={(
                                <RequireAccess itemId={'lbCustSearch'}>
                                    <CustomTestsV1 />
                                </RequireAccess>
                            )} path={'/custom-tests'}
                        >
                            <Route index element={<CustomTestsSearchV1 />} />
                            <Route element={<CustomTestEditV1 />} path={'edit'} />
                        </Route>

                        {/* Custom Tests 2.0 (V2) — separate URL, reached from the V1 banner. */}
                        <Route
                            element={(
                                <RequireAccess itemId={'lbCustSearch'}>
                                    <Pages.CustomTests />
                                </RequireAccess>
                            )} path={'/custom-tests-v2'}
                        >
                            <Route index element={<Pages.CustomTestsSearch />} />
                            <Route element={<Pages.CustomTestWizard mode={'create-smart'} />} path={'create'} />
                            <Route element={<Pages.CustomTestWizard mode={'copy'} />} path={'copy/:testId'} />
                            <Route element={<Pages.CustomTestWizard mode={'edit'} />} path={'build/:testId'} />
                            <Route element={<Pages.CustomTestEdit />} path={'edit/:testId'} />
                        </Route>

                        <Route element={<Pages.EditSectionTest />} path={'edit-section-test'}/>

                        <Route
                            element={(
                                <RequireAccess itemId={'lblFeedbackViews'}>
                                    <Pages.Feedback />
                                </RequireAccess>
                            )} path={'feedback'}
                        />

                        <Route path={'features'}>
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbFeatureRingManageUser'}>
                                        <Pages.FeatureRingManageUser />
                                    </RequireAccess>
                                )} path={'access'}
                            />
                            <Route path={'manage'}>
                                <Route
                                    index
                                    element={(
                                        <RequireAccess itemId={'lbFeatureFlags'}>
                                            <Pages.FeatureFlag />
                                        </RequireAccess>
                                    )}
                                />
                                <Route
                                    element={(
                                        <RequireAccess itemId={'lbFeatureFlags'}>
                                            <Pages.FeatureFlag />
                                        </RequireAccess>
                                    )} path={':id'}
                                />
                            </Route>
                        </Route>

                        <Route path={'/classrooms'}>
                            <Route
                                index
                                element={(
                                    <RequireAccess itemId={'lbStudGroupSearch'}>
                                        <Pages.Groups />
                                    </RequireAccess>
                                )}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbStudGroupSearch'}>
                                        <Pages.Groups />
                                    </RequireAccess>
                                )} path={':id'}
                            />
                        </Route>

                        <Route
                            element={(
                                <RequireAccess itemId={'lbIntegrations'}>
                                    <Pages.Integrations />
                                </RequireAccess>
                            )} path={'integrations'}
                        />

                        {/* The rebuilt Integrations page was served at /integrations-v2 while the
                            wizard-era V1 still owned /integrations. V1 is gone, so keep the old
                            address working for anyone who bookmarked it. */}
                        <Route element={<RedirectWithQuery to={'/integrations'}/>} path={'integrations-v2'} />

                        <Route element={<Pages.LTIResourceKeys />} path={'lti-resource-keys'} />

                        <Route path={'/licenses'}>
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbMenuLicSearchEdit'}>
                                        <Pages.LicenseSearch />
                                    </RequireAccess>
                                )} path={'search'}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbMenuLicSearchEdit'}>
                                        <Pages.LicenseContentManagement />
                                    </RequireAccess>
                                )} path={'content-management'}
                            />
                        </Route>

                        <Route path={'/orders'}>
                            <Route
                                element={(
                                    <RequireAccess itemId={'lblExtendOrder'}>
                                        <Pages.ExtendOrder />
                                    </RequireAccess>
                                )} path={'extend'}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbOrdView'}>
                                        <Pages.OrderHistory />
                                    </RequireAccess>
                                )} path={'history'}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbMyInventory'}>
                                        <Pages.MyInventory />
                                    </RequireAccess>
                                )} path={'inventory'}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lblFulfillmentManager'}>
                                        <Pages.FulfillmentManager />
                                    </RequireAccess>
                                )} path={'manage'}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbOrdCreate'}>
                                        <Pages.PlaceOrder />
                                    </RequireAccess>
                                )} path={'place'}
                            >
                                <Route index element={<Pages.PlaceOrderWizard />}/>
                            </Route>
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbOrdPending'}>
                                        <Pages.PendingOrders />
                                    </RequireAccess>
                                )} path={'pending'}
                            >
                                <Route index element={<Pages.PendingOrdersList />} />
                            </Route>
                        </Route>

                        <Route path={'/partners'}>
                            <Route index element={<PartnersRouteSwitch />} />
                            <Route element={ <Pages.SiteBranding />} path={'branding'}/>
                            <Route element={ <Pages.PartnerCreateEdit />} path={'create'}>
                                <Route index element={<Pages.PartnerCreateEditWizard />} />
                            </Route>
                            <Route element={ <Pages.PartnerLookup />} path={'lookup'}/>
                            <Route element={<Pages.Partners />} path={':id'}/>
                        </Route>

                        <Route path={'reports'} >
                            <Route
                                index element={(
                                    <RequireAccess itemId={'ddlRep'}>
                                        <Pages.Reports />
                                    </RequireAccess>
                                )}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'ddlRep'}>
                                        <Pages.Reports />
                                    </RequireAccess>
                                )} path={':reportId'}
                            />
                        </Route>

                        <Route path={'/students'}>
                            <Route
                                index element={(
                                    <RequireAccess itemId={'lbStudentsSearch'}>
                                        <Pages.Students />
                                    </RequireAccess>
                                )}
                            />
                            <Route element={<Pages.ImportStudents />} path={'import'} />
                            <Route element={<Pages.MergeStudents />} path={'merge'} />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbStudentsSearch'}>
                                        <Pages.Students />
                                    </RequireAccess>
                                )} path={':id'}
                            />
                        </Route>

                        <Route path={'users'}>
                            <Route index element={<Navigate replace to={'/administrators'} />} />
                        </Route>

                        <Route path={'administrators'}>
                            <Route
                                index element={(
                                    <RequireAccess itemId={'ViewAccounts'}>
                                        <Pages.SystemAdministrators />
                                    </RequireAccess>
                                )}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'ViewAccounts'}>
                                        <Pages.SystemAdministrators />
                                    </RequireAccess>
                                )} path={':id'}
                            />
                        </Route>

                        <Route path={'/sku-management'}>
                            <Route
                                index
                                element={(
                                    <RequireAccess itemId={'lbSKUManagementSearch'}>
                                        <Pages.SKUManagement />
                                    </RequireAccess>
                                )}
                            />
                            <Route
                                element={(
                                    <RequireAccess itemId={'lbSKUManagementSearch'}>
                                        <Pages.SKUManagement />
                                    </RequireAccess>
                                )} path={':id'}
                            />
                        </Route>

                        <Route element={<Pages.Login />} path={'/login'} />

                        <Route element={<Pages.ForgotPassword />} path={'/forgot-password'} />

                        <Route element={<Pages.RecoverUsername />} path={'/recover-username'} />

                        <Route element={<Pages.SetPassword mode={'reset'} />} path={'/reset-password'} />

                        <Route element={<Pages.SetPassword mode={'welcome'} />} path={'/welcome'} />

                        <Route element={<Pages.Error404 />} path={'/*'} />

                    </GuardedRoutes>
                </HelpSidebar>
            </RouteGuard>
        </BrowserRouter>
    );

};